import React, { useState, Component } from 'react';
/**
 * GovernanceRulesTab — Main governance rules browser.
 *
 * Three views: validation rules, rulebook files, and policy thresholds.
 * Canonical location: portals/admin/content/governance/ (DDD — UI in portal)
 */
import { SearchIcon, ShieldCheckIcon, FilterIcon, FolderIcon, CodeIcon, SlidersIcon } from 'lucide-react';
import type { Severity, Stage, ViewId } from './governanceRulesData';
import { RULES, RULEBOOK_FILES, PIPELINE_FILES, STAGE_LABELS, STAGE_COLORS, ALL_STAGES } from './governanceRulesData';
import { RuleCard, RulebookCard, ThresholdSection } from './GovernanceRulesCards';
import { Input } from '../../../../components/ui/Input';
import { Select } from '../../../../components/ui/Select';
// ─── View Definitions ─────────────────────────────────────────
const VIEWS: {
  id: ViewId;
  label: string;
  icon: React.ReactNode;
}[] = [{
  id: 'regels',
  label: 'Validatieregels',
  icon: <ShieldCheckIcon className="h-4 w-4" data-id="element-3981" />
}, {
  id: 'regelboeken',
  label: 'Regelboeken',
  icon: <FolderIcon className="h-4 w-4" data-id="element-3982" />
}, {
  id: 'drempelwaarden',
  label: 'Drempelwaarden',
  icon: <SlidersIcon className="h-4 w-4" data-id="element-3983" />
}];
// ─── Severity Counts (static) ─────────────────────────────────
const BLOCKER_COUNT = RULES.filter(r => r.severity === 'BLOCKER').length;
const WARNING_COUNT = RULES.filter(r => r.severity === 'WARNING').length;
const INFO_COUNT = RULES.filter(r => r.severity === 'INFO').length;
// ─── Component ────────────────────────────────────────────────
export function GovernanceRulesTab() {
  const [view, setView] = useState<ViewId>('regels');
  const [search, setSearch] = useState('');
  const [selectedStage, setSelectedStage] = useState<Stage | 'all'>('all');
  const [selectedSeverity, setSelectedSeverity] = useState<Severity | 'all'>('all');
  const filtered = RULES.filter(rule => {
    const matchesSearch = search === '' || rule.label.toLowerCase().includes(search.toLowerCase()) || rule.description.toLowerCase().includes(search.toLowerCase()) || rule.id.toLowerCase().includes(search.toLowerCase());
    const matchesStage = selectedStage === 'all' || rule.stage === selectedStage;
    const matchesSeverity = selectedSeverity === 'all' || rule.severity === selectedSeverity;
    return matchesSearch && matchesStage && matchesSeverity;
  });
  return <div className="space-y-5" data-id="element-3984">
      {/* View Switcher */}
      <div className="flex gap-1 rounded-xl border border-gray-100 bg-white p-1" data-id="element-3985">
        {VIEWS.map(v => <button key={v.id} onClick={() => setView(v.id)} className={`flex flex-1 items-center justify-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-all ${view === v.id ? 'bg-blue-600 text-white shadow-sm' : 'text-gray-500 hover:bg-gray-50 hover:text-gray-700'}`} data-id="element-3986">
            {v.icon}
            <span className="hidden sm:inline" data-id="element-3987">{v.label}</span>
          </button>)}
      </div>

      {/* ── View: Validatieregels ── */}
      {view === 'regels' && <>
          {/* Summary */}
          <div className="grid grid-cols-3 gap-3" data-id="element-3988">
            <div className="rounded-lg border border-red-100 bg-red-50 p-3 text-center" data-id="element-3989">
              <p className="text-2xl font-bold text-red-700" data-id="element-3990">{BLOCKER_COUNT}</p>
              <p className="text-xs font-medium text-red-600" data-id="element-3991">
                Blokkerende Regels
              </p>
            </div>
            <div className="rounded-lg border border-amber-100 bg-amber-50 p-3 text-center" data-id="element-3992">
              <p className="text-2xl font-bold text-amber-700" data-id="element-3993">
                {WARNING_COUNT}
              </p>
              <p className="text-xs font-medium text-amber-600" data-id="element-3994">
                Waarschuwingsregels
              </p>
            </div>
            <div className="rounded-lg border border-blue-100 bg-blue-50 p-3 text-center" data-id="element-3995">
              <p className="text-2xl font-bold text-blue-700" data-id="element-3996">{INFO_COUNT}</p>
              <p className="text-xs font-medium text-blue-600" data-id="element-3997">
                Informatieve Regels
              </p>
            </div>
          </div>

          {/* Filters */}
          <div className="flex flex-col gap-3 sm:flex-row" data-id="element-3998">
            <div className="flex-1" data-id="element-3999">
              <Input type="text" placeholder="Zoek regels op naam, beschrijving of ID..." value={search} onChange={e => setSearch(e.target.value)} icon={<SearchIcon className="w-4 h-4" data-id="element-4001" />} variant="admin" size="sm" data-id="element-4000" />
            </div>
            <Select value={selectedSeverity} onChange={e => setSelectedSeverity(e.target.value as Severity | 'all')} options={[{
          value: 'all',
          label: 'Alle ernst'
        }, {
          value: 'BLOCKER',
          label: 'Blokkerend'
        }, {
          value: 'WARNING',
          label: 'Waarschuwing'
        }, {
          value: 'INFO',
          label: 'Informatief'
        }]} variant="admin" size="sm" fullWidth={false} data-id="element-4002" />
          </div>

          {/* Stage Filter Pills */}
          <div className="flex flex-wrap gap-2" data-id="element-4003">
            <FilterIcon className="h-4 w-4 text-gray-400 self-center" data-id="element-4004" />
            {ALL_STAGES.map(stage => <button key={stage} onClick={() => setSelectedStage(stage)} className={`rounded-full px-3 py-1 text-xs font-medium transition-all ${selectedStage === stage ? 'bg-gray-800 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} data-id="element-4005">
                {stage === 'all' ? 'Alle fasen' : STAGE_LABELS[stage as Stage]}
              </button>)}
          </div>

          <p className="text-sm text-gray-500" data-id="element-4006">
            {filtered.length} van {RULES.length} regels weergegeven
          </p>

          <div className="space-y-2" data-id="element-4007">
            {filtered.length === 0 ? <div className="rounded-lg border border-gray-100 bg-gray-50 py-12 text-center" data-id="element-4008">
                <SearchIcon className="mx-auto h-8 w-8 text-gray-300 mb-2" data-id="element-4009" />
                <p className="text-gray-500" data-id="element-4010">
                  Geen regels gevonden voor deze zoekopdracht.
                </p>
              </div> : filtered.map(rule => <RuleCard key={rule.id} rule={rule} data-id="element-4011" />)}
          </div>
        </>}

      {/* ── View: Regelboeken ── */}
      {view === 'regelboeken' && <div className="space-y-5" data-id="element-4012">
          <div className="rounded-lg border border-gray-100 bg-gray-50 p-4" data-id="element-4013">
            <p className="text-sm text-gray-600" data-id="element-4014">
              De governance-regels zijn verdeeld over{' '}
              <strong data-id="element-4015">regelboek-bestanden</strong> (markdown) en{' '}
              <strong data-id="element-4016">pipeline-bronbestanden</strong> (TypeScript). Klik op een
              kaart om de kernregels te bekijken.
            </p>
          </div>

          {/* Rulebook files */}
          <div data-id="element-4017">
            <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-500 mb-3 flex items-center gap-2" data-id="element-4018">
              <FolderIcon className="h-4 w-4" data-id="element-4019" />
              Regelboek Bestanden (Markdown)
            </h3>
            <div className="space-y-2" data-id="element-4020">
              {RULEBOOK_FILES.map(book => <RulebookCard key={book.id} book={book} data-id="element-4021" />)}
            </div>
          </div>

          {/* Pipeline source files */}
          <div data-id="element-4022">
            <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-500 mb-3 flex items-center gap-2" data-id="element-4023">
              <CodeIcon className="h-4 w-4" data-id="element-4024" />
              Pipeline Bronbestanden (TypeScript)
            </h3>
            <div className="rounded-xl border border-gray-100 bg-white overflow-hidden" data-id="element-4025">
              {PIPELINE_FILES.map((file, i) => <div key={file.path} className={`flex items-start gap-3 px-4 py-3 ${i < PIPELINE_FILES.length - 1 ? 'border-b border-gray-50' : ''}`} data-id="element-4026">
                  <span className={`rounded-full px-2 py-0.5 text-xs font-medium flex-shrink-0 mt-0.5 ${STAGE_COLORS[file.stage]}`} data-id="element-4027">
                    {STAGE_LABELS[file.stage]}
                  </span>
                  <div className="min-w-0 flex-1" data-id="element-4028">
                    <p className="text-sm font-semibold text-gray-800" data-id="element-4029">
                      {file.name}
                    </p>
                    <code className="text-xs text-gray-400 font-mono" data-id="element-4030">
                      {file.path}
                    </code>
                    <p className="text-xs text-gray-500 mt-0.5" data-id="element-4031">
                      {file.description}
                    </p>
                  </div>
                </div>)}
            </div>
          </div>
        </div>}

      {/* ── View: Drempelwaarden ── */}
      {view === 'drempelwaarden' && <ThresholdSection data-id="element-4032" />}
    </div>;
}